import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { AuthProvider } from "@/context/AuthContext";
import { trackVisit, trackPageView } from "@/lib/analytics";
import Home from "@/pages/Home";

const ChatWidget = lazy(() => import("@/components/ChatWidget").then((m) => ({ default: m.ChatWidget })));
const Programs = lazy(() => import("@/pages/Programs"));
const About = lazy(() => import("@/pages/About"));
const Locations = lazy(() => import("@/pages/Locations"));
const BookTour = lazy(() => import("@/pages/BookTour"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));

const PageFallback = () => <div className="min-h-screen bg-cream" aria-busy="true" />;

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackVisit(pathname);
    trackPageView(pathname);
  }, [pathname]);
  return null;
};

const PAGE_META = {
  "/": ["Daycare & Preschool in Katy TX | Learning & Beyond", "Learning & Beyond Academy — daycare, preschool and Pre-K for ages 6 weeks to 12 years in Katy, TX. Two centers on Clay Rd. and N Fry Rd. with school transportation. Book a tour today."],
  "/programs": ["Infant, Toddler, Pre-K & After-School Programs in Katy TX | Learning & Beyond", "Infant care, toddler and Pre-K programs, plus before & after school care with bus transportation to 13 Katy elementary schools. CCS scholarships accepted."],
  "/about": ["Our Philosophy — Reggio Emilia-Inspired Childcare in Katy | Learning & Beyond", "Curiosity-led, Reggio Emilia-inspired learning with WatchMeGrow camera access at Learning & Beyond Academy in Katy, TX."],
  "/locations": ["Daycare Locations: Clay Rd. & N Fry Rd., Katy TX | Learning & Beyond", "Two childcare centers in Katy, TX — 19600 Clay Rd. and 3750 N Fry Rd. Open Mon–Fri 6 AM–6 PM. Texas Rising Star 4-star rated."],
  "/gallery": ["Photo Gallery — Classrooms, Playgrounds & Buses | Learning & Beyond Katy", "Take a look inside Learning & Beyond Academy's Clay Road and N Fry Road childcare centers in Katy, TX."],
  "/privacy": ["Privacy Policy | Learning & Beyond", "How Learning & Beyond collects, uses, shares and protects personal information submitted through our website, ads and enrollment inquiries."],
  "/book": ["Book a Daycare Tour in Katy TX | Learning & Beyond", "Schedule a 30-minute tour or send an enrollment inquiry for our Clay Rd. or N Fry Rd. center in Katy, TX."],
};

const setMeta = (name, content) => {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.name = name;
    document.head.appendChild(tag);
  }
  tag.content = content;
};

const Shell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  useEffect(() => {
    setMeta("robots", isAdmin ? "noindex, nofollow" : "index, follow");
    const [title, description] = PAGE_META[pathname] || (isAdmin ? ["Staff Console | Learning & Beyond", ""] : PAGE_META["/"]);
    document.title = title;
    if (description) setMeta("description", description);
  }, [pathname, isAdmin]);
  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Navbar />}
      <main>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/about" element={<About />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/book" element={<BookTour />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
        </Suspense>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <Suspense fallback={null}><ChatWidget /></Suspense>}
    </>
  );
};

function App() {
  const [lenis, setLenis] = useState(null);

  useEffect(() => {
    const instance = new Lenis({ lerp: 0.1, wheelMultiplier: 0.95 });
    setLenis(instance);
    let rafId;
    const raf = (time) => {
      instance.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      instance.destroy();
    };
  }, []);

  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
  }, [lenis]);

  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Shell />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
